import styled from '@emotion/styled'
import {
  Box,
  Chip,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import { VirtualElement } from '@popperjs/core'
import { useState } from 'react'
import { useActiveOrganization } from 'hooks/use-organization'
import CheckIcon from '@mui/icons-material/Check'
import { useSearchTags } from 'hooks/use-tags'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { getColorForDarkMode } from 'utils'
import { COLORS } from 'app-constants'

type Props = {
  anchorEl?: null | VirtualElement | (() => VirtualElement)
  onSelectTags: (tags: string[]) => void
  selectedTags: string[]
  onClose: () => void
}

interface PopperComponentProps {
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const PopperComponent = (props: PopperComponentProps) => {
  const { disablePortal, anchorEl, open, ...other } = props
  return <StyledAutocompletePopper {...other} />
}

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    fontSize: 16,
    fontWeight: 700,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    padding: 0,
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

const FloatingTagFilterPicker = (props: Props) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const isOpen = !!props.anchorEl

  const [activeOrganization] = useActiveOrganization()

  const [recentTags, setRecentTags] = useState<string[]>(() => {
    const recentTags = localStorage.getItem('jelled_recentTags')
    if (!recentTags) {
      return []
    }
    return JSON.parse(recentTags)
  })

  const { setQuery, pendingInputValue, options, setOptions, isLoading, debounceQueryUpdate } = useSearchTags()

  if (!isOpen) {
    return null
  }

  return (
    <Paper
      sx={{
        width: isMobile ? 'calc(100vw - 32px)' : '480px',
      }}>
      <ClickAwayListener onClickAway={() => props.onClose()}>
        <Box sx={{ width: isMobile ? 'calc(100vw - 32px)' : '480px', paddingTop: '8px' }}>
          <Autocomplete
            fullWidth
            loading={isLoading}
            disablePortal
            PopperComponent={PopperComponent}
            multiple
            filterSelectedOptions
            getOptionLabel={(option) => option}
            noOptionsText={'No matching tag found'}
            isOptionEqualToValue={(option, value) => option === value}
            options={options}
            autoComplete
            disabled={false}
            value={props.selectedTags}
            disableClearable
            popupIcon={''}
            onChange={(event, newValue) => {
              setOptions([...newValue, ...options])

              props.onSelectTags(newValue.map((a) => a))

              const recentTagsArr = [...newValue.reverse(), ...recentTags.filter((tag) => !newValue.includes(tag))]
              const recentTagsObj = JSON.stringify(recentTagsArr)
              localStorage.setItem('jelled_recentTags', recentTagsObj)
              setRecentTags(recentTagsArr)
            }}
            onInputChange={(event, newInputValue) => {
              setQuery(newInputValue)

              if (!activeOrganization) {
                return
              }

              pendingInputValue.current = newInputValue
              debounceQueryUpdate(newInputValue, activeOrganization)
            }}
            renderTags={(value, getTagProps) => {
              const numTags = value.length
              const limitTags = 10

              return (
                <>
                  {value.slice(0, limitTags).map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={option}
                      sx={{
                        '& .MuiChip-label': {
                          color: COLORS.PURE_WHITE,
                        },
                        '& .MuiChip-deleteIcon': {
                          color: COLORS.PURE_WHITE,
                        },
                      }}
                    />
                  ))}

                  {numTags > limitTags && ` +${numTags - limitTags}`}
                </>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for tag"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: COLORS.GRAY_3, mr: '-8px' }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{
              '& .MuiOutlinedInput-root': {
                pb: '4px',
                pt: '4px',
                pl: '16px',
              },
              '& .MuiAutocomplete-tag': {
                backgroundColor: COLORS.GRAY_0,
              },
              '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                boxShadow: 'none',
                borderBottom: '1px solid #ddd',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              '& + .MuiAutocomplete-popper .MuiAutocomplete-noOptions': {
                color: COLORS.GRAY_3,
                pt: 1,
                pb: 1,
              },
              '& + .MuiAutocomplete-popper .MuiAutocomplete-paper': {
                borderRadius: 0,
              },
            }}
          />

          {recentTags.length > 0 && (
            <Stack my={1} sx={{ width: '100%' }}>
              <Typography
                variant="pXS"
                py={1}
                px={2}
                sx={{
                  color: getColorForDarkMode(theme),
                }}>
                Recent tag:
              </Typography>
              {recentTags.slice(0, 5).map((tag) => (
                <TagRow
                  key={tag}
                  tag={tag}
                  selectedTags={props.selectedTags}
                  onSelect={(tag: string) => {
                    if (props.selectedTags.includes(tag)) {
                      props.onSelectTags([...props.selectedTags.filter((t) => t !== tag)])
                    } else {
                      props.onSelectTags([...props.selectedTags, tag])
                    }
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </ClickAwayListener>
    </Paper>
  )
}

const RowStack = styled(Stack)`
  width: 100%;
  padding: 8px 16px;
  cursor: pointer;
`

const getBackgroundColor = (isHovered: boolean, isSelected: boolean, theme: Theme) => {
  if (theme.palette.mode === 'light') {
    if (isHovered) {
      return '#f8f8f8'
    }
    if (isSelected) {
      return '#222222'
    }
  } else {
    if (isHovered) {
      return '#f8f8f8'
    }
    if (isSelected) {
      return '#222222'
    }
  }

  return 'white'
}

const getTextColor = (isHovered: boolean, isSelected: boolean, theme: Theme) => {
  if (theme.palette.mode === 'light') {
    if (isHovered && isSelected) {
      return '#222222'
    }
    if (isHovered && !isSelected) {
      return '#222222'
    }
    if (!isHovered && isSelected) {
      return 'white'
    }
    return '#222222'
  } else {
    if (isHovered && isSelected) {
      return '#222222'
    }
    if (isHovered && !isSelected) {
      return '#222222'
    }
    if (!isHovered && isSelected) {
      return '#ffffff'
    }
    return '#ffffff'
  }
}

const getIconColor = (isHovered: boolean, isSelected: boolean) => {
  if (isHovered && isSelected) {
    return '#222222'
  }
  if (isHovered && !isSelected) {
    return '#222222'
  }
  if (!isHovered && isSelected) {
    return 'white'
  }
  return '#222222'
}

const TagRow = ({
  tag,
  onSelect,
  selectedTags,
}: {
  tag: string
  onSelect: (tag: string) => void
  selectedTags: string[]
}) => {
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  const isSelected = selectedTags.includes(tag)
  return (
    <RowStack
      direction="row"
      alignItems="center"
      spacing={1}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        onSelect(tag)
      }}
      sx={{
        backgroundColor: getBackgroundColor(isHovered, isSelected, theme),
      }}>
      <Stack alignItems="center" width="100%" direction="row" spacing={1}>
        <Typography
          variant="h5"
          style={{
            color: getTextColor(isHovered, isSelected, theme),
          }}>
          {tag}
        </Typography>
      </Stack>
      <CheckIcon
        style={{
          color: getIconColor(isHovered, isSelected),
          visibility: isSelected ? 'visible' : 'hidden',
        }}
      />
    </RowStack>
  )
}

export default FloatingTagFilterPicker
